/**
 * AssociatedItemsModal - Modal showing items associated with a modifier or modifier group
 * 
 * Story 1.7: Updated for modal + iframe coexistence
 * 
 * Features:
 * - Modal title with modifier/modifier group name
 * - Search bar to filter items by name or description
 * - Paginated list of associated items (10 per page)
 * - Clickable item rows (Story 1.7 enhancement) - can click anywhere on row to open preview
 * - Each item shows name, description, and "View" button
 * - Close button, ESC key, and click-outside behavior to close modal
 * - Focus trap and accessibility features (ARIA labels)
 * - Scrollable content area
 * - Modal can stay open while iframe preview shows alongside it
 */

/* eslint-disable react/prop-types, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions, jsx-a11y/no-redundant-roles */
import React, { useState, useEffect, useRef } from "react";
import { VALIDATION_TYPES } from "../../utils/enums";

const AssociatedItemsModal = ({ validationItem, onClose, baseLanguage, menuId, onOpenPanel, isPreviewOpen }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  const itemsPerPage = 10;
  const { associatedItems = [], name, type } = validationItem;

  /**
   * Filter items based on search term (case-insensitive)
   */
  const filteredItems = associatedItems.filter(item =>
    (item.itemName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.itemDescription?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  /**
   * Paginate filtered items
   */
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /**
   * Reset to page 1 when search term changes
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  /**
   * Focus trap for accessibility
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Tab") {
        // Simple focus trap - keep focus within modal
        const focusableElements = modalRef.current?.querySelectorAll(
          "button, input, [tabindex]:not([tabindex='-1'])"
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements.at(-1);

          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              e.preventDefault();
              if (lastElement) {
                lastElement.focus();
              }
            }
          } else if (document.activeElement === lastElement) {
            e.preventDefault();
            if (firstElement) {
              firstElement.focus();
            }
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    // Focus the first element on mount for accessibility
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  /**
   * Handle click outside modal to close
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Story 1.7: Open item detail in slide-out panel
   * NEW: Modal and iframe can coexist when preview opened from modal
   * (panel stays visible beside/overlay the modal)
   */
  const handleViewItem = (item) => {
    // Transform associated item structure to match validation result structure
    const itemData = {
      id: item.itemId,
      name: item.itemName,
      categoryId: item.categoryId
    };
    
    // NEW: Keep modal open and show preview alongside it (sourceContext = 'modal')
    // Don't call onClose() - modal stays visible
    onOpenPanel(itemData);
  };

  /**
   * Get modal type label
   */
  const getTypeLabel = () => {
    return type === VALIDATION_TYPES.MODIFIER_GROUP ? "Modifier Group" : "Modifier";
  };

  return (
    <div
      className={`ls-validation-modal-overlay ${isPreviewOpen ? 'ls-validation-modal-overlay--with-preview' : ''}`}
      onClick={handleOverlayClick}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
      role="presentation"
      tabIndex={-1}
    >
      <div
        className={`ls-validation-modal ${isPreviewOpen ? 'ls-validation-modal--with-preview' : ''}`}
        ref={modalRef}
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="ls-validation-modal-header">
          <div>
            <h2 id="modal-title" className="ls-validation-modal-title">
              {name}
            </h2>
            <p className="ls-validation-modal-subtitle">
              {`Associated Items (${getTypeLabel()})`}
            </p>
          </div>
          <button
            ref={firstFocusableRef}
            className="ls-validation-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            ×
          </button>
        </div>

        {/* Search Bar */}
        <div className="ls-validation-modal-search">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ls-validation-modal-search-input"
            aria-label="Search items by name or description"
          />
        </div>

        {/* Items List or Empty State */}
        <div className="ls-validation-modal-items">
          {filteredItems.length === 0 ? (
            <div className="ls-validation-modal-empty">
              {searchTerm ? (
                <p>No items found matching "{searchTerm}"</p>
              ) : (
                <p>No associated items available.</p>
              )}
            </div>
          ) : (
            paginatedItems.map((item) => (
              <div
                key={item.itemId}
                className="ls-validation-modal-item"
                onClick={() => handleViewItem(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleViewItem(item);
                  }
                }}
              >
                <div className="ls-validation-modal-item-content">
                  <h3 className="ls-validation-modal-item-name">
                    {item.itemName}
                  </h3>
                  <p className="ls-validation-modal-item-description">
                    {item.itemDescription}
                  </p>
                  {item.categoryName && (
                    <p className="ls-validation-modal-item-category">
                      {item.categoryName}
                    </p>
                  )}
                </div>
                <button
                  className="ls-validation-modal-item-view"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click from triggering
                    handleViewItem(item);
                  }}
                  aria-label={`View ${item.itemName}`}
                  type="button"
                >
                  View {'›'}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {filteredItems.length > itemsPerPage && (
          <div className="ls-validation-modal-pagination">
            <button
              className="ls-validation-modal-pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              aria-label="Previous page"
              type="button"
            >
              {'‹'} Previous
            </button>
            <span className="ls-validation-modal-pagination-info">
              {currentPage} / {totalPages}
            </span>
            <button
              ref={lastFocusableRef}
              className="ls-validation-modal-pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              aria-label="Next page"
              type="button"
            >
              Next {'›'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssociatedItemsModal;
